import React, { useState } from 'react';
import { Header } from '../components/Header';
import { BreedFilter } from '../components/BreedFilter';
import { PuppyCard } from '../components/PuppyCard';
import { PuppyForm } from '../components/PuppyForm';
import { DreamPuppySection } from '../components/DreamPuppySection';
import { StudsSection } from '../components/StudsSection';
import { LittersSection } from '../components/LittersSection';
import { BREEDS, BUSINESS_INFO } from '../data/mock';
import { puppyService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoginPage from './LoginPage';
import { AdminStats } from '../components/AdminStats';
import { analyticsService } from '../services/analyticsApi';
import { Plus, PawPrint, Loader2, Star, Heart, Shield, Stethoscope, UserCheck, Award, MessageCircle, Home } from 'lucide-react';

const Catalog = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [selectedBreed, setSelectedBreed] = useState(null);
  const [puppies, setPuppies] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPuppy, setEditingPuppy] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load puppies from backend
  const loadPuppies = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await puppyService.getAll();
      setPuppies(data || []);
    } catch (err) {
      setPuppies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPuppies();
  }, [loadPuppies]);

  // Track page visit (once per session)
  React.useEffect(() => {
    const visited = sessionStorage.getItem('catalog_visited');
    if (!visited) {
      analyticsService.recordVisit();
      sessionStorage.setItem('catalog_visited', '1');
    }
  }, []);

  // If user is now admin, hide login page
  React.useEffect(() => {
    if (isAdmin) setShowLogin(false);
  }, [isAdmin]);

  // Separate puppies by category
  const regularPuppies = puppies.filter(p => p.breed !== 'cesion-adulto' && p.breed !== 'adopcion-responsable');
  const adultCessions = puppies.filter(p => p.breed === 'cesion-adulto');
  const adoptionPuppies = puppies.filter(p => p.breed === 'adopcion-responsable');

  const filteredPuppies = selectedBreed
    ? (['cesion-adulto', 'adopcion-responsable'].includes(selectedBreed) ? [] : regularPuppies.filter(p => p.breed === selectedBreed))
    : regularPuppies;

  const filteredAdults = selectedBreed === 'cesion-adulto' || !selectedBreed ? adultCessions : [];
  const filteredAdoptions = selectedBreed === 'adopcion-responsable' || !selectedBreed ? adoptionPuppies : [];

  const handleSavePuppy = async (puppyData) => {
    try {
      if (editingPuppy) {
        const updated = await puppyService.update(puppyData.id, puppyData);
        setPuppies(puppies.map(p => p.id === puppyData.id ? updated : p));
      } else {
        const created = await puppyService.create(puppyData);
        setPuppies([...puppies, created]);
      }
      setEditingPuppy(null);
    } catch (err) {
      void err;
      alert('Error al guardar el cachorro');
    }
  };

  const handleDeletePuppy = async (id) => {
    try {
      await puppyService.delete(id);
      setPuppies(puppies.filter(p => p.id !== id));
    } catch (err) {
      void err;
      alert('Error al eliminar el cachorro');
    }
  };

  const handleEditPuppy = (puppy) => {
    setEditingPuppy(puppy);
    setIsFormOpen(true);
  };

  const handleAddWithBreed = (breed) => {
    setEditingPuppy(null);
    setFormData_breed(breed);
    setIsFormOpen(true);
  };
  // Store pre-selected breed for the form
  const [formData_breed, setFormData_breed] = useState(null);

  const stats = {
    total: regularPuppies.length,
    available: regularPuppies.filter(p => p.status === 'disponible').length,
    reserved: regularPuppies.filter(p => p.status === 'reservado').length
  };

  // Show login page
  if (showLogin && !isAdmin) {
    return <LoginPage onBack={() => setShowLogin(false)} />;
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-amber-800">Cargando cachorros...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Header onLoginClick={() => setShowLogin(true)} />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4">
            <PawPrint className="w-4 h-4" />
            <span className="text-sm font-medium">Criadores profesionales en Benidorm</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mb-3">
            Cachorros de Raza Premium
          </h2>
          <p className="text-amber-700 max-w-2xl mx-auto">
            Encuentra tu companero perfecto. Todos nuestros cachorros son criados con amor, 
            con garantia de salud y documentacion completa.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-100">
            <p className="text-3xl font-bold text-amber-800">{stats.total}</p>
            <p className="text-amber-600 text-sm">Total Cachorros</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-emerald-100">
            <p className="text-3xl font-bold text-emerald-600">{stats.available}</p>
            <p className="text-emerald-600 text-sm">Disponibles</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-100">
            <p className="text-3xl font-bold text-amber-500">{stats.reserved}</p>
            <p className="text-amber-500 text-sm">Reservados</p>
          </div>
        </div>

        {/* Admin Stats Panel */}
        {isAdmin && <AdminStats />}

        {/* Admin Add Button */}
        {isAdmin && (
          <div className="mb-6">
            <button
              data-testid="add-puppy-btn"
              onClick={() => { setEditingPuppy(null); setIsFormOpen(true); }}
              className="flex items-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              Anadir Nuevo Cachorro
            </button>
          </div>
        )}

        {/* Breed Filter */}
        <BreedFilter 
          selectedBreed={selectedBreed} 
          onSelectBreed={setSelectedBreed} 
        />

        {/* Puppies Grid */}
        {filteredPuppies.length > 0 ? (
          <div data-testid="puppies-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPuppies.map(puppy => (
              <PuppyCard 
                key={puppy.id} 
                puppy={puppy} 
                isAdmin={isAdmin}
                onEdit={handleEditPuppy}
              />
            ))}
          </div>
        ) : selectedBreed !== 'cesion-adulto' ? (
          <div className="text-center py-16">
            <PawPrint className="w-16 h-16 mx-auto text-amber-300 mb-4" />
            <h3 className="text-xl font-semibold text-amber-800 mb-2">
              No hay cachorros {selectedBreed && `de ${BREEDS.find(b => b.id === selectedBreed)?.name}`}
            </h3>
            <p className="text-amber-600">
              {isAdmin ? 'Anade nuevos cachorros con el boton de arriba' : 'Vuelve pronto para ver nuevas camadas'}
            </p>
          </div>
        ) : null}

        {/* ========== UPCOMING LITTERS SECTION ========== */}
        <div className="mt-12">
          <LittersSection isAdmin={isAdmin} />
        </div>

        {/* ========== ADULT CESSION SECTION ========== */}
        {(filteredAdults.length > 0 || isAdmin) && (
          <section className="mt-16">
            {/* Elegant Divider */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1"></div>
              <Star className="w-6 h-6 text-rose-400" />
              <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1"></div>
            </div>

            {/* Section Header */}
            <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-rose-100 rounded-3xl p-8 mb-8 border border-rose-200 shadow-lg">
              <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-white/80 text-rose-700 px-4 py-2 rounded-full mb-4 shadow-sm">
                  <Award className="w-4 h-4" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Cesion Selectiva</span>
                </div>
                
                <h2 className="text-2xl md:text-3xl font-bold text-rose-900 mb-4">
                  Ejemplar de Elite de Alto Valor Genetico
                </h2>
                
                <p className="text-rose-800 leading-relaxed mb-6 text-lg italic">
                  "Ejemplar adulto disponible en cesion selectiva, de alto valor genetico y excelente calidad, 
                  procedente de lineas cuidadosamente seleccionadas. Destinado exclusivamente a entorno familiar, 
                  en condiciones de adopcion responsable."
                </p>
                
                <div className="bg-white/70 rounded-2xl p-6 text-left space-y-4">
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-rose-500 mt-1 flex-shrink-0" />
                    <p className="text-rose-900">
                      <span className="font-semibold">Ejemplares de elite</span> que han contribuido a nuestro programa 
                      de cria selectiva y ahora merecen el carino exclusivo de una familia.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Stethoscope className="w-5 h-5 text-rose-500 mt-1 flex-shrink-0" />
                    <p className="text-rose-900">
                      <span className="font-semibold">Compromiso de esterilizacion obligatoria</span> por parte del nuevo 
                      propietario en nuestra clinica concertada, garantizando su bienestar como mascota de compania.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-rose-500 mt-1 flex-shrink-0" />
                    <p className="text-rose-900">
                      <span className="font-semibold">Contribucion de gastos veterinarios y cuidados.</span> Son animales 
                      de extraordinaria calidad genetica y excelente morfologia.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Add Button for Cesion */}
            {isAdmin && (
              <div className="mb-6">
                <button
                  data-testid="add-cesion-btn"
                  onClick={() => handleAddWithBreed('cesion-adulto')}
                  className="flex items-center gap-2 bg-rose-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-rose-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Plus className="w-5 h-5" />
                  Anadir Ejemplar de Elite
                </button>
              </div>
            )}

            {/* Adult Cessions Grid */}
            {filteredAdults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAdults.map(puppy => (
                  <div key={puppy.id} className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-rose-200 to-pink-200 rounded-2xl -z-10 transform translate-x-1 translate-y-1"></div>
                    <PuppyCard 
                      puppy={puppy} 
                      isAdmin={isAdmin}
                      onEdit={handleEditPuppy}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-white/50 rounded-2xl">
                <Award className="w-10 h-10 mx-auto text-rose-300 mb-2" />
                <p className="text-rose-600 text-sm">No hay ejemplares disponibles actualmente</p>
              </div>
            )}
          </section>
        )}

        {/* ========== ADOPCION RESPONSABLE SECTION ========== */}
        {(filteredAdoptions.length > 0 || isAdmin) && (
          <section className="mt-16">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent flex-1"></div>
              <PawPrint className="w-6 h-6 text-teal-400" />
              <div className="h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent flex-1"></div>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-br from-teal-50 via-emerald-50 to-teal-100 rounded-3xl p-8 mb-8 border border-teal-200 shadow-lg">
              <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-white/80 text-teal-700 px-4 py-2 rounded-full mb-4 shadow-sm">
                  <PawPrint className="w-4 h-4" />
                  <span className="text-sm font-semibold uppercase tracking-wider">Adopcion Responsable</span>
                </div>
                
                <h2 className="text-2xl md:text-3xl font-bold text-teal-900 mb-2">
                  Ejemplares no aptos para venta
                </h2>
                
                <p className="text-teal-800 leading-relaxed mb-6 text-base">
                  En Luxury Puppies Kennel Benidorm creemos que cada animal merece una vida feliz y un hogar 
                  donde recibir carino, cuidados y respeto.
                </p>

                <div className="bg-white/70 rounded-2xl p-6 text-left space-y-4">
                  <p className="text-teal-900">
                    Por diferentes circunstancias, algunos ejemplares pueden no ser aptos para su comercializacion 
                    o para incorporarse a nuestro programa de cria. Puede tratarse de pequenas particularidades fisicas, 
                    caracteristicas que los hacen no aptos para reproduccion, o cualquier otra circunstancia que determine 
                    que su destino no sea la venta convencional.
                  </p>
                  <p className="text-teal-900">
                    Estos animales no se ofrecen como cachorros de venta ni se comercializan como ejemplares de cria. 
                    Cuando su estado y caracter lo permiten, buscamos para ellos una familia adecuada mediante un 
                    proceso de <span className="font-semibold">adopcion responsable</span>.
                  </p>

                  <div className="border-t border-teal-200 pt-4 space-y-4">
                    <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                      <p className="text-teal-900 font-semibold italic">
                        Nuestro objetivo es encontrarles un hogar definitivo, estable y comprometido con su bienestar.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <UserCheck className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                      <p className="text-teal-900">
                        La entrega se realizara tras valorar la idoneidad de la familia y explicar de forma transparente 
                        las caracteristicas y circunstancias particulares de cada ejemplar.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Stethoscope className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                      <p className="text-teal-900">
                        En aquellos casos en los que sea necesario, la adopcion podra estar condicionada a la 
                        <span className="font-semibold"> esterilizacion</span>, con el objetivo de garantizar que el animal 
                        sea destinado exclusivamente a compania.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                      <p className="text-teal-900">
                        La familia adoptante debera comprometerse a proporcionar los cuidados veterinarios, alimentacion, 
                        atencion y condiciones de bienestar adecuadas durante toda la vida del animal.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Home className="w-5 h-5 text-teal-600 mt-1 flex-shrink-0" />
                      <p className="text-teal-900">
                        No todos los ejemplares estan disponibles para adopcion y cada caso se estudia individualmente.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                      <p className="text-teal-900">
                        <span className="font-semibold">La adopcion no es gratuita.</span> Se requiere una aportacion 
                        para cubrir los gastos veterinarios generados durante la vida del ejemplar.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-teal-200 pt-4 text-center">
                    <p className="text-teal-800 font-bold text-lg">
                      <PawPrint className="w-5 h-5 inline-block mr-2 text-teal-600" />
                      Porque no buscamos simplemente un hogar.
                    </p>
                    <p className="text-teal-700 font-semibold italic mt-1">
                      Buscamos el hogar adecuado para cada uno de ellos.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Add Button for Adopcion */}
            {isAdmin && (
              <div className="mb-6">
                <button
                  data-testid="add-adopcion-btn"
                  onClick={() => handleAddWithBreed('adopcion-responsable')}
                  className="flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Plus className="w-5 h-5" />
                  Anadir Ejemplar en Adopcion
                </button>
              </div>
            )}

            {/* Adoption Grid */}
            {filteredAdoptions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAdoptions.map(puppy => (
                  <div key={puppy.id} className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-200 to-emerald-200 rounded-2xl -z-10 transform translate-x-1 translate-y-1"></div>
                    <PuppyCard 
                      puppy={puppy} 
                      isAdmin={isAdmin}
                      onEdit={handleEditPuppy}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-white/50 rounded-2xl">
                <PawPrint className="w-10 h-10 mx-auto text-teal-300 mb-2" />
                <p className="text-teal-600 text-sm">No hay ejemplares disponibles en adopcion responsable actualmente</p>
              </div>
            )}
          </section>
        )}

        {/* Studs Section */}
        <div className="mt-12">
          <StudsSection isAdmin={isAdmin} />
        </div>

        {/* Dream Puppy Service */}
        <DreamPuppySection />

        {/* WhatsApp Float Button (non-admin) */}
        {!isAdmin && (
          <a
            data-testid="whatsapp-float-btn"
            href={`https://wa.me/34${BUSINESS_INFO.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-6 right-6 bg-emerald-500 text-white p-4 rounded-full shadow-2xl hover:bg-emerald-600 transition-all duration-300 hover:scale-110 z-50"
            title="Contactanos por WhatsApp"
          >
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-amber-900 text-amber-100 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <img 
            src={BUSINESS_INFO.logo} 
            alt={BUSINESS_INFO.name}
            className="h-20 w-20 mx-auto mb-4 rounded-full bg-white p-1"
          />
          <h3 className="text-xl font-bold text-white mb-2">{BUSINESS_INFO.name}</h3>
          <p className="text-amber-200 mb-4">Criadores de cachorros premium en Benidorm</p>
          <p className="text-amber-300">
            Telefono: {BUSINESS_INFO.whatsapp.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}
          </p>
          <p className="text-amber-400 text-sm mt-4">&copy; 2025 Todos los derechos reservados</p>
        </div>
      </footer>

      {/* Puppy Form Modal */}
      <PuppyForm
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingPuppy(null); setFormData_breed(null); }}
        onSave={handleSavePuppy}
        onDelete={handleDeletePuppy}
        editingPuppy={editingPuppy}
        preSelectedBreed={formData_breed}
      />
    </div>
  );
};

export default Catalog;
